/**
 * ViewerPage
 * Standalone read-only map viewer with multi-floor support
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Map as MapIcon } from '@mui/icons-material'
import ProjectPageHeader from '../../components/ProjectPageHeader'
import { useTheme } from '../../map-editor-shared/context/ThemeContext'
import { useProjectStore } from '../../map-editor-shared/store/projectStore'
import { useFloorStore } from '../../map-editor-shared/store/floorStore'
import { useViewerStore } from '../../map-editor-shared/store/viewerStore'
import { useProjectInitializer } from '../../map-editor-shared/hooks/useProjectInitializer'
import { statusService, useStatusStore } from '../../map-editor-features/status'
import { LoadingOverlay } from '../../map-editor-shared/ui/LoadingOverlay'
import { ViewerJsonPanel } from '../editor/components/ViewerJsonPanel'
import { ConnectionPanel } from './components/ConnectionPanel'
import { CctvAlert } from './components/CctvAlert'
import { FloorSelectorPanel, MultiFloorCanvas, CanvasFloorViewer } from '../../map-editor-features/viewer'
import styles from './ViewerPage.module.css'
import { log, LogCategory } from '../../map-editor-shared/lib/logger'

export default function ViewerPage() {
  const { projectId: urlProjectId, mapId } = useParams<{ projectId: string; mapId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { theme, toggleTheme } = useTheme()

  // mapId 는 백엔드의 lotId (예: 443) 이며 동시에
  //   - localStorage 의 project 식별자
  //   - /v0.1/map-data/project-data/{id} API 의 id 파라미터
  // 양쪽 모두로 사용된다. urlProjectId(=siteId, 예: 2) 는 대시보드 라우팅용일 뿐
  // map-data API 와는 무관하다. (siteId 로 호출하면 빈 data 가 반환돼
  // "Project not found in API" 가 발생함.)
  const projectId = mapId
  const backendProjectId = mapId  // map-data API 는 lotId(=mapId) 로 조회

  // Project initialization with API fallback
  const { loading: initLoading, error: initError, project } = useProjectInitializer(projectId, backendProjectId)

  // Stores
  const { floors } = useFloorStore()
  const {
    selectedFloorIds,
    setSelectedFloorIds,
    floorDisplayOrder,
    setFloorDisplayOrder,
    resetSelection,
  } = useViewerStore()

  // State
  const [projectData, setProjectData] = useState<any>(null)
  const [showConnectionPanel, setShowConnectionPanel] = useState(false)
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(false)
  const [rightSidebarCollapsed, setRightSidebarCollapsed] = useState(false)
  // Beta: render selected floors via the canvas engine instead of JointJS SVG,
  // to validate canvas-mode fidelity + performance against the existing viewer.
  const [canvasBeta, setCanvasBeta] = useState(false)

  // Status service (connection is manual via ConnectionPanel)
  const { disconnect } = useStatusStore()

  // Reset viewer state when projectId changes to prevent stale data
  useEffect(() => {
    resetSelection()
    setProjectData(null)
  }, [projectId, resetSelection])

  // Load project data from store (after initialization hook)
  useEffect(() => {
    // Wait for initialization to complete
    if (initLoading) return

    if (!projectId || !project) {
      return
    }


    const projectFloors = floors.filter(floor => floor.lotId === projectId)

    if (projectFloors.length === 0) {
      setProjectData({
        projectId,
        projectName: project.name,
        floors: [],
        graphJson: null
      })
      return
    }

    // Sort floors by order
    const sortedFloors = [...projectFloors].sort((a, b) => a.order - b.order)


    setProjectData({
      projectId,
      projectName: project.name,
      floors: sortedFloors,
    })

    // Initialize URL state - select all floors by default
    const urlFloors = searchParams.get('floors')?.split(',').filter(id => id.trim() !== '') || []


    if (urlFloors.length > 0) {
      const validFloorIds = urlFloors.filter(id => sortedFloors.find(f => f.id === id))
      const invalidFloorIds = urlFloors.filter(id => !sortedFloors.find(f => f.id === id))


      // Show warning if URL has floor IDs that don't exist in store
      if (invalidFloorIds.length > 0) {
      }

      if (validFloorIds.length > 0) {
        setSelectedFloorIds(validFloorIds)
      } else {
        setSelectedFloorIds(sortedFloors.map(f => f.id))
      }
    } else {
      setSelectedFloorIds(sortedFloors.map(f => f.id))
    }

    // Initialize floor display order based on natural sort order
    const initialOrder = sortedFloors.map(f => f.id)
    setFloorDisplayOrder(initialOrder)
  }, [projectId, project, initLoading, floors, setFloorDisplayOrder])

  // Update URL when viewer state changes
  useEffect(() => {
    if (!projectData) return
    // Skip if no floors are selected yet (initial load)
    if (selectedFloorIds.length === 0) return

    const params = new URLSearchParams()
    params.set('floors', selectedFloorIds.join(','))

    setSearchParams(params, { replace: true })
  }, [selectedFloorIds, projectData, setSearchParams])

  // Initialize status service for all floors with floor-specific data
  // Note: Connection is handled manually via ConnectionPanel (서버 연결 버튼)
  useEffect(() => {
    if (!projectData?.floors) return

    // Group CCTV and parking IDs by floor
    const objectsByFloor: { floorId: string; cctvIds: string[]; parkingIds: string[] }[] = []

    projectData.floors.forEach((floor: any) => {
      if (!floor.mapData?.graphJson?.cells) return

      const cctvIds: string[] = []
      const parkingIds: string[] = []

      // Extract IDs from graph cells with floor namespace
      floor.mapData.graphJson.cells.forEach((cell: any) => {
        if (!cell.id || cell.type === 'standard.Link') return

        const id = String(cell.id)
        const type = cell.type || cell.objectType || ''

        // Add floor namespace to create unique IDs across all floors
        const namespacedId = `${floor.id}::${id}`

        // Check if CCTV
        if (
          type === 'Cctv' ||
          type === 'cctv' ||
          id.includes('cctv') ||
          id.includes('onepassreader') ||
          id.includes('reader')
        ) {
          cctvIds.push(namespacedId)
        }

        // Check if Parking
        if (
          type === 'ParkingLocation' ||
          type === 'parkingLocation' ||
          type === 'parking' ||
          id.includes('parking')
        ) {
          parkingIds.push(namespacedId)
        }
      })

      objectsByFloor.push({
        floorId: floor.id,
        cctvIds,
        parkingIds,
      })
    })


    // Only initialize object IDs - connection is manual via ConnectionPanel
    statusService.initialize(objectsByFloor)

    // Cleanup on unmount - disconnect if connected
    return () => {
      disconnect()
    }
  }, [projectData, disconnect])

  // Handle floor selection change
  const handleFloorSelectionChange = (floorIds: string[]) => {
    setSelectedFloorIds(floorIds)
  }

  // Get selected floors for display, sorted by floorDisplayOrder
  // Note: We no longer filter out floors without data - MultiFloorCanvas handles empty floors
  // with appropriate "맵 데이터 없음" UI
  const getSelectedFloors = () => {
    if (!projectData?.floors) return []

    // Get all selected floors (no validation filtering)
    const selectedFloors = projectData.floors
      .filter((f: any) => selectedFloorIds.includes(f.id))

    // Log floor data for debugging
    selectedFloors.forEach((f: any) => {
      const hasGraphJson = f.mapData?.graphJson &&
                           f.mapData.graphJson.cells &&
                           f.mapData.graphJson.cells.length > 0
      const hasCsvData = f.mapData?.csvParsedData || f.mapData?.csvRawData

    })


    // Sort by floorDisplayOrder if available
    if (floorDisplayOrder.length > 0) {
      return selectedFloors.sort((a: any, b: any) => {
        const aIndex = floorDisplayOrder.indexOf(a.id)
        const bIndex = floorDisplayOrder.indexOf(b.id)
        // If not found in order, put at end
        const aOrder = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex
        const bOrder = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex
        return aOrder - bOrder
      })
    }

    return selectedFloors
  }

  // Count floors with and without data (graphJson OR CSV) for display
  const getFloorStats = () => {
    if (!projectData?.floors) return { total: 0, withData: 0, withoutData: 0 }

    const withData = projectData.floors.filter((f: any) => {
      const hasGraphJson = f.mapData?.graphJson?.cells?.length > 0
      const hasCsvData = f.mapData?.csvParsedData || f.mapData?.csvRawData
      return hasGraphJson || hasCsvData
    }).length
    const total = projectData.floors.length

    return {
      total,
      withData,
      withoutData: total - withData
    }
  }

  // Show loading during initialization
  if (initLoading) {
    return <LoadingOverlay message="프로젝트 로딩 중..." />
  }

  // Show error if initialization failed
  if (initError) {
    return (
      <div className={styles.error}>
        <h2>프로젝트를 불러올 수 없습니다</h2>
        <p>{initError}</p>
        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
          <button onClick={() => window.location.reload()}>
            다시 시도
          </button>
          <button onClick={() => navigate(`/project/${urlProjectId}/map-editor`)}>
            대시보드로 돌아가기
          </button>
        </div>
      </div>
    )
  }

  // Show error if project data not ready
  if (!projectData) {
    return <LoadingOverlay message="프로젝트 데이터 준비 중..." />
  }

  const selectedFloors = getSelectedFloors()

  return (
    <div className={styles.container}>
      {/* 표준 헤더 — 다른 페이지와 동일한 ProjectPageHeader (현장 목록 › 현장 › 맵 뷰어). */}
      <ProjectPageHeader currentPageLabel="맵 뷰어" currentPageIcon={<MapIcon fontSize="inherit" />} />
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backButton}
            onClick={() => navigate(`/project/${urlProjectId}/map-editor`)}
          >
            ← 대시보드
          </button>
          <h1 className={styles.title}>
            {projectData.projectName || 'Unnamed Project'} (뷰어)
          </h1>
        </div>

        <div className={styles.headerRight}>
          <button
            className={styles.connectionButton}
            onClick={() => setCanvasBeta((v) => !v)}
            aria-pressed={canvasBeta}
            title="캔버스 렌더러로 전환 (실험적)"
          >
            {canvasBeta ? '🖼️ 캔버스(beta) ✓' : '🖼️ 캔버스(beta)'}
          </button>
          <button
            className={styles.connectionButton}
            onClick={() => setShowConnectionPanel(true)}
            aria-label="Connection and Share"
          >
            🔌 연결 & 공유
          </button>
          <button
            className={styles.themeButton}
            onClick={toggleTheme}
            aria-label="Toggle theme"
          >
            {theme === 'light' ? '🌙' : '☀️'}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        {/* Floor Selector Panel (left sidebar) */}
        {projectData.floors.length > 1 && !leftSidebarCollapsed && (
          <div className={styles.floorSelectorPanel}>
            <FloorSelectorPanel
              floors={projectData.floors}
              selectedFloorIds={selectedFloorIds}
              onSelectionChange={handleFloorSelectionChange}
              displayOrder={floorDisplayOrder}
              onOrderChange={setFloorDisplayOrder}
              maxSelection={projectData.floors.length}
            />
          </div>
        )}

        {/* Left Sidebar Toggle Button */}
        {projectData.floors.length > 1 && (
          <button
            className={`${styles.sidebarToggleLeft} ${leftSidebarCollapsed ? styles.collapsed : ''}`}
            onClick={() => setLeftSidebarCollapsed(!leftSidebarCollapsed)}
            aria-label={leftSidebarCollapsed ? 'Show left sidebar' : 'Hide left sidebar'}
          >
            {leftSidebarCollapsed ? '▶' : '◀'}
          </button>
        )}

        {/* Canvas Area */}
        <div className={styles.canvasArea}>
          {selectedFloors.length > 0 ? (
            canvasBeta ? (
              <div
                style={{
                  display: 'grid',
                  gap: 8,
                  width: '100%',
                  height: '100%',
                  gridTemplateColumns: `repeat(${selectedFloors.length === 1 ? 1 : selectedFloors.length <= 4 ? 2 : 3}, 1fr)`,
                  gridAutoRows: '1fr',
                }}
              >
                {selectedFloors.map((floor) => (
                  <div key={floor.id} style={{ position: 'relative', minWidth: 0, minHeight: 0 }}>
                    <CanvasFloorViewer floor={floor} />
                  </div>
                ))}
              </div>
            ) : (
              <MultiFloorCanvas
                floors={selectedFloors}
                layout="grid"
              />
            )
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: '16px',
              padding: '48px',
              textAlign: 'center',
              color: 'var(--color-text-secondary)'
            }}>
              {(() => {
                const stats = getFloorStats()
                const selectedCount = selectedFloorIds.length

                // Case 1: No floors at all in the project
                if (stats.total === 0) {
                  return (
                    <>
                      <p style={{fontSize: '18px', fontWeight: 600, color: 'var(--color-text-primary)'}}>
                        프로젝트에 층이 없습니다
                      </p>
                      <span style={{fontSize: '14px'}}>
                        에디터에서 층을 생성해 주세요
                      </span>
                    </>
                  )
                }

                // Case 2: Floors exist but none are selected
                if (selectedCount === 0) {
                  return (
                    <>
                      <p style={{fontSize: '18px', fontWeight: 600, color: 'var(--color-text-primary)'}}>
                        선택된 층이 없습니다
                      </p>
                      <span style={{fontSize: '14px'}}>
                        왼쪽 패널에서 층을 선택해 주세요
                      </span>
                    </>
                  )
                }

                // Case 3: Floors selected but they don't have graphJson data
                if (stats.withoutData > 0) {
                  return (
                    <>
                      <p style={{fontSize: '18px', fontWeight: 600, color: 'var(--color-text-primary)'}}>
                        맵 데이터가 없는 층이 있습니다
                      </p>
                      <span style={{fontSize: '14px'}}>
                        선택한 {selectedCount}개 층 중 {stats.withoutData}개 층에 데이터가 없습니다.
                      </span>
                      <span style={{fontSize: '14px', color: '#ff9800', fontWeight: 600, marginTop: '12px', display: 'block'}}>
                        ⚠️ 왼쪽 패널에서 "데이터 없음" 표시된 층에 CSV/DXF 파일을 업로드해주세요
                      </span>
                      <span style={{fontSize: '13px', color: 'var(--color-text-tertiary)', marginTop: '12px'}}>
                        참고: URL 공유 시 같은 브라우저에서만 데이터가 유지됩니다.<br/>
                        다른 브라우저에서는 프로젝트를 다시 생성해야 합니다.
                      </span>
                    </>
                  )
                }

                // Default case
                return (
                  <>
                    <p style={{fontSize: '18px', fontWeight: 600, color: 'var(--color-text-primary)'}}>
                      표시할 데이터가 없습니다
                    </p>
                    <span style={{fontSize: '14px'}}>
                      층 데이터를 확인해 주세요
                    </span>
                  </>
                )
              })()}
            </div>
          )}
        </div>

        {/* Right Sidebar - JSON Preview */}
        {!rightSidebarCollapsed && (
          <div className={styles.jsonPanel}>
            <ViewerJsonPanel
              graph={null}
              projectName={projectData.projectName || 'Unnamed Project'}
              floors={selectedFloors}
            />
          </div>
        )}

        {/* Right Sidebar Toggle Button */}
        <button
          className={`${styles.sidebarToggleRight} ${rightSidebarCollapsed ? styles.collapsed : ''}`}
          onClick={() => setRightSidebarCollapsed(!rightSidebarCollapsed)}
          aria-label={rightSidebarCollapsed ? 'Show right sidebar' : 'Hide right sidebar'}
        >
          {rightSidebarCollapsed ? '◀' : '▶'}
        </button>
      </main>

      {/* Connection Panel */}
      {showConnectionPanel && (
        <ConnectionPanel
          onClose={() => setShowConnectionPanel(false)}
        />
      )}

      {/* CCTV Disconnection Alert */}
      <CctvAlert />
    </div>
  )
}
